import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ShieldCheck, Tv, Eye, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import SectionHeading from "@/components/SectionHeading";
import { useContent } from "@/context/ContentContext";

const PLAY_STRUCTURE = "/brand/play-structure.png";

export default function About() {
  const { content } = useContent();
  const about = content?.about || {};
  const highlights = [
    { icon: ShieldCheck, title: "Safe & Private", desc: "Every booking is 100% private. No strangers, no crowds, just your group." },
    { icon: Tv, title: "Dedicated Adult Seating", desc: "Comfortable seating with TVs so grown-ups can relax and watch the game." },
    { icon: Eye, title: "Full Visibility", desc: "Adults keep a clear view of the play area at all times. Peace of mind, guaranteed." },
    { icon: Sparkles, title: "Spotless & Modern", desc: "A brand-new, meticulously clean facility built for unforgettable celebrations." },
  ];
  return (
    <div className="pt-32 pb-24">
      <Seo title="About | Keys Event Center" description="Keys Event Center is a private-reservation indoor family entertainment venue in Keys / Park Hill, Oklahoma, safe, clean, and built for families." />
      <div className="mx-auto max-w-7xl px-6">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <p className="eyebrow mb-3">About Us</p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-tight mb-6 text-[#f5f0ff]">{about.title}</h1>
            <p className="text-[#9aa0c0] leading-relaxed text-lg">{about.body}</p>
            <ul className="mt-6 space-y-3">
              {(about.points || []).map((p) => (
                <li key={p} className="flex items-start gap-3 text-[#d6d3ee]"><Check className="text-[#21e6cf] shrink-0 mt-0.5" size={18} /> {p}</li>
              ))}
            </ul>
            <Link to="/reservations" className="btn-primary inline-flex px-8 py-4 mt-8">Reserve Your Party</Link>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} className="relative">
            <div className="animate-float rounded-[2rem] overflow-hidden border border-[#2a2a44] shadow-xl">
              <img src={PLAY_STRUCTURE} alt="Keys Event Center interior" className="w-full object-cover" />
            </div>
          </motion.div>
        </div>

        <div className="mt-24">
          <SectionHeading eyebrow="Why Families Choose Us" title="Parents Can Finally Relax" />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {highlights.map((h, i) => (
              <motion.div key={h.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="card card-lift p-7">
                <div className="h-12 w-12 grid place-items-center rounded-xl bg-[#0e2036] text-[#21e6cf] mb-4"><h.icon size={24} /></div>
                <h3 className="font-display text-lg font-bold mb-2 text-[#f5f0ff]">{h.title}</h3>
                <p className="text-sm text-[#9aa0c0]">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
